import { useEffect, useState } from "react";
import type { TournamentSummary } from "../lib/api";
import "./tournament-picker.css";

type Props = {
  tournaments: TournamentSummary[];
  season: number;
  loading?: boolean;
  onOpen: (id: string, season: number) => void;
};

const UPCOMING_PAGE_SIZE = 4;

function todayIso() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function dateLabel(item: TournamentSummary) {
  if (!item.startDate) return "Termin do ustalenia";
  return item.endDate ? `${item.startDate} — ${item.endDate}` : item.startDate;
}

function TournamentCard({ item, tone, onOpen }: { item: TournamentSummary; tone: "live" | "upcoming" | "archive"; onOpen: Props["onOpen"] }) {
  return (
    <button type="button" className={`premium-event-card ${tone}`} onClick={() => onOpen(item.id, item.season)}>
      <div className="premium-event-card-top">
        <span className={`premium-event-badge ${tone}`}>{tone === "live" ? "TEN TYDZIEŃ" : tone === "upcoming" ? "NADCHODZI" : "ARCHIWUM"}</span>
        <span className="premium-event-date">{dateLabel(item)}</span>
      </div>
      <strong>{item.name}</strong>
      <div className="premium-event-card-bottom">
        <span>{tone === "live" ? "Aktywny turniej" : tone === "upcoming" ? "Przygotuj kolejne wydarzenie" : "Otwórz historyczny turniej"}</span>
        <b>Otwórz <span aria-hidden="true">→</span></b>
      </div>
    </button>
  );
}

export function TournamentPicker({ tournaments, season, loading = false, onOpen }: Props) {
  const [upcomingPage, setUpcomingPage] = useState(0);
  const today = todayIso();
  const sorted = [...tournaments].sort((a, b) => (a.startDate || "9999-12-31").localeCompare(b.startDate || "9999-12-31") || a.name.localeCompare(b.name, "pl"));
  const live = sorted.filter((item) => Boolean(item.startDate && item.endDate && item.startDate <= today && item.endDate >= today));
  const upcoming = sorted.filter((item) => Boolean(item.startDate && item.startDate > today));
  const archive = sorted.filter((item) => Boolean(item.endDate && item.endDate < today));
  const undated = sorted.filter((item) => !item.startDate || !item.endDate);
  const primary = live[0] || upcoming[0];
  const remainingUpcoming = upcoming.filter((item) => item.id !== primary?.id);
  const upcomingPageCount = Math.max(1, Math.ceil(remainingUpcoming.length / UPCOMING_PAGE_SIZE));
  const safeUpcomingPage = Math.min(upcomingPage, upcomingPageCount - 1);
  const near = remainingUpcoming.slice(safeUpcomingPage * UPCOMING_PAGE_SIZE, (safeUpcomingPage + 1) * UPCOMING_PAGE_SIZE);
  const archiveRows = [...archive, ...undated];

  useEffect(() => { setUpcomingPage(0); }, [season]);
  useEffect(() => {
    if (upcomingPage >= upcomingPageCount) setUpcomingPage(Math.max(0, upcomingPageCount - 1));
  }, [upcomingPage, upcomingPageCount]);

  if (loading) return <div className="premium-picker-state">Wczytuję turnieje…</div>;
  if (!sorted.length) return <div className="premium-picker-state">Brak turniejów w sezonie {season}.</div>;

  return (
    <div className="premium-tournament-picker">
      {primary ? (
        <section className="premium-picker-live">
          <div className="premium-picker-section-heading">
            <div><span>{live.length ? "TERAZ" : "NAJBLIŻSZY"}</span><h3>{live.length ? "Ten tydzień" : "Najbliższy turniej"}</h3></div>
            <small>{live.length ? "Najważniejszy kontekst operacyjny" : "Brak aktywnego turnieju w dzisiejszej dacie"}</small>
          </div>
          <TournamentCard item={primary} tone={live.length ? "live" : "upcoming"} onOpen={onOpen} />
        </section>
      ) : null}

      {remainingUpcoming.length ? (
        <section className="premium-picker-upcoming">
          <div className="premium-picker-section-heading">
            <div><span>DALEJ</span><h3>Nadchodzące</h3></div>
            <small>{remainingUpcoming.length} kolejnych wydarzeń</small>
          </div>
          <div className="premium-picker-grid">{near.map((item) => <TournamentCard key={item.id} item={item} tone="upcoming" onOpen={onOpen} />)}</div>
          {upcomingPageCount > 1 ? (
            <nav className="premium-picker-pagination" aria-label="Kolejne turnieje">
              <button type="button" disabled={safeUpcomingPage === 0} onClick={() => setUpcomingPage((page) => Math.max(0, page - 1))}>← Poprzednie</button>
              <span>Strona <strong>{safeUpcomingPage + 1}</strong> z <strong>{upcomingPageCount}</strong></span>
              <button type="button" disabled={safeUpcomingPage >= upcomingPageCount - 1} onClick={() => setUpcomingPage((page) => Math.min(upcomingPageCount - 1, page + 1))}>Następne →</button>
            </nav>
          ) : null}
        </section>
      ) : null}

      {archiveRows.length ? (
        <details className="premium-picker-archive">
          <summary><div><span>ARCHIWUM</span><strong>Starsze turnieje</strong><small>{archiveRows.length} wydarzeń · domyślnie ukryte</small></div><b aria-hidden="true">⌄</b></summary>
          <div className="premium-picker-grid archive">{archiveRows.map((item) => <TournamentCard key={item.id} item={item} tone="archive" onOpen={onOpen} />)}</div>
        </details>
      ) : null}
    </div>
  );
}
